import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { CheckboxComponent, DropdownItem, DropdownTriggerDirective, SwitchComponent } from '@libs/ui-lib';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { TranslateModule } from '@ngx-translate/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { localStorageKey } from '@libs/utils';

@Component({
  selector: 'lib-schedule-filter',
  imports: [CommonModule, OverlayModule, CheckboxComponent, SwitchComponent, TranslateModule, ReactiveFormsModule],
  templateUrl: './schedule-filter.component.html',
  styleUrl: './schedule-filter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScheduleFilterComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  public overlayPositionStrategy: ConnectedPosition[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
      offsetY: 10,
    },
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
      offsetY: -10,
    },
  ];

  @Input() public isShowcase: boolean = false;

  @Output() public readonly selectOperatory = new EventEmitter<{ value: string[] | string | null; isAllToggled?: boolean }>();
  @Output() public readonly filterTentative = new EventEmitter<boolean>();
  @Output() public readonly showCompactView = new EventEmitter<boolean>();
  @Output() public readonly hidePatientName = new EventEmitter<boolean>();

  protected readonly dropdownOpen = signal(false);

  @ContentChild(DropdownTriggerDirective) public dropdownTrigger!: never;

  @Input() public set operatories(value: DropdownItem<string>[]) {
    this.dropdownItems.set([...value]);
  }
  protected readonly dropdownItems = signal<DropdownItem<string>[]>([]);

  @Input() public set selectedOperatories(value: string[]) {
    this.writeValue(value);
  }
  protected readonly dropdownValue = signal<DropdownItem<string>[] | null>(null);

  @Input() public set showTentative(value: boolean) {
    this.formGroup.patchValue({ showTentative: value });
  }

  protected readonly formGroup = new FormGroup({
    showTentative: new FormControl<boolean>(JSON.parse(localStorage.getItem(localStorageKey.showTentativeAppointments) || 'true')),
    isCompactView: new FormControl<boolean>(JSON.parse(localStorage.getItem(localStorageKey.isCompactView) || 'false')),
    hidePatientName: new FormControl<boolean>(JSON.parse(localStorage.getItem(localStorageKey.hidePatientName) || 'false')),
  });

  public ngOnInit(): void {
    this.formGroup.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({ showTentative, isCompactView, hidePatientName }) => {
      this.filterTentative.emit(!!showTentative);
      this.showCompactView.emit(!!isCompactView);
      this.hidePatientName.emit(!!hidePatientName);
    });
  }

  protected toggleDropdown(): void {
    this.dropdownOpen.update((isOpen) => {
      return !isOpen;
    });
  }

  protected toggleSelectAll(): void {
    if (this.isAllSelected()) {
      this.dropdownValue.set([]);
      this.selectOperatory.emit({ value: [], isAllToggled: true });
    } else {
      this.dropdownValue.set(this.dropdownItems());
      this.selectOperatory.emit({ value: this.dropdownItems().map((dv) => dv.value), isAllToggled: true });
    }
  }

  protected onItemClick(item: DropdownItem<string>): void {
    const currentValue = (this.dropdownValue() as DropdownItem<string>[]) ?? [];
    if (this.isItemSelected(item)) {
      this.dropdownValue.set(currentValue.filter((dv) => dv.value !== item.value));
    } else {
      this.dropdownValue.set([...currentValue, item]);
    }

    const itemValues = (this.dropdownValue() as DropdownItem<string>[]).map((dv) => dv.value);
    this.selectOperatory.emit({ value: itemValues });
  }

  public writeValue(value: string[]): void {
    const items = this.dropdownItems().filter((x) => value.includes(x.value));

    this.dropdownValue.set(items);
  }

  public isAllSelected(): boolean {
    if (this.dropdownValue() === null) return false;
    return this.dropdownItems().length === (this.dropdownValue() as DropdownItem<string>[]).length;
  }

  public isItemSelected(item: DropdownItem<string>): boolean {
    if (this.dropdownValue() === null) return false;
    return (this.dropdownValue() as DropdownItem<string>[]).some((dv) => dv.value === item.value);
  }
}
